import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { PreSaleBanner } from '../components/layout/PreSaleBanner';
import { SEO } from '../components/SEO';
import {
    Package, Users, Gift, FileText, CreditCard, Mail,
    RefreshCw, Truck, Send, Trash2, AlertCircle, Plus, Edit3, Eye, EyeOff,
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
    { key: 'orders', icon: Package, label: 'Orders' },
    { key: 'customers', icon: Users, label: 'Customers' },
    { key: 'referrals', icon: Gift, label: 'Referrals' },
    { key: 'articles', icon: FileText, label: 'Articles' },
    { key: 'notify', icon: Mail, label: 'Notify' },
    { key: 'subscriptions', icon: CreditCard, label: 'Subscriptions' },
];

function Stat({ label, value, accent = false }) {
    return (
        <div className={`card-base p-5 ${accent ? 'border-2 border-primary' : ''}`}>
            <div className={`text-xs uppercase tracking-widest font-bold mb-2 ${accent ? 'text-primary' : 'text-muted-foreground'}`}>
                {label}
            </div>
            <div className={`font-heading text-3xl font-black leading-none ${accent ? 'text-primary' : 'text-foreground'}`}>
                {value}
            </div>
        </div>
    );
}

function OrdersStats({ authHeader }) {
    const [m, setM] = useState(null);
    const [running, setRunning] = useState(false);
    const [lastRun, setLastRun] = useState(null);

    const load = () => {
        axios.get(`${API}/admin/metrics`, { headers: authHeader() })
            .then(r => setM(r.data)).catch(console.error);
    };
    useEffect(() => { load(); }, []);

    const runReviewSweep = async () => {
        setRunning(true);
        try {
            const r = await axios.post(`${API}/admin/cron/review-emails`, {}, { headers: authHeader() });
            setLastRun(r.data);
            load();
        } catch (e) {
            alert('Sweep failed: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setRunning(false);
        }
    };

    const fmt = (n) => `€${(n || 0).toFixed(2)}`;
    return (
        <div className="mb-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat label="Total revenue" value={m ? fmt(m.total_revenue) : '…'} accent />
                <Stat label="Total orders" value={m ? m.total_orders : '…'} />
                <Stat label="Refund rate" value={m ? `${m.refund_rate}%` : '…'} />
            </div>
            <div className="card-base p-4 flex items-start justify-between gap-4 flex-wrap">
                <div className="max-w-xl">
                    <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1">
                        Post-purchase review emails
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                        Hourly background job emails customers 14 days after their order shipped.
                        Sent: <strong>{m?.review_emails_sent ?? 0}</strong> · Pending: <strong>{m?.review_emails_pending ?? 0}</strong>
                        {lastRun && (
                            <span className="text-primary font-semibold ml-2" data-testid="review-sweep-result">
                                · Last run: {lastRun.sent}/{lastRun.processed}
                            </span>
                        )}
                    </p>
                </div>
                <button
                    onClick={runReviewSweep}
                    disabled={running}
                    className="text-xs font-semibold bg-foreground text-white rounded-lg px-3 py-2 flex items-center gap-1.5 disabled:opacity-60"
                    data-testid="run-review-sweep"
                >
                    <Send size={12} /> {running ? 'Running…' : 'Run review-email sweep'}
                </button>
            </div>
        </div>
    );
}

function Orders({ authHeader }) {
    const [orders, setOrders] = useState([]);
    const [q, setQ] = useState('');
    const [actionId, setActionId] = useState(null);

    const load = async () => {
        const res = await axios.get(`${API}/admin/orders${q ? `?q=${encodeURIComponent(q)}` : ''}`, { headers: authHeader() });
        setOrders(res.data);
    };
    useEffect(() => { load(); }, []);
    const ship = async (id) => {
        setActionId(id);
        try { await axios.post(`${API}/admin/orders/${id}/ship`, {}, { headers: authHeader() }); await load(); }
        finally { setActionId(null); }
    };
    const refund = async (id) => {
        if (!window.confirm('Refund this order? This will call Stripe and mark the order refunded.')) return;
        setActionId(id);
        try { await axios.post(`${API}/admin/orders/${id}/refund`, {}, { headers: authHeader() }); await load(); }
        finally { setActionId(null); }
    };
    const resend = async (id) => {
        setActionId(id);
        try { await axios.post(`${API}/admin/orders/${id}/resend-confirmation`, {}, { headers: authHeader() }); alert('Confirmation email queued.'); }
        finally { setActionId(null); }
    };

    const badge = (status) => {
        const colours = {
            confirmed: 'bg-yellow-100 text-yellow-800',
            shipped: 'bg-green-100 text-green-800',
            refunded: 'bg-red-100 text-red-800',
        };
        return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colours[status] || 'bg-slate-100 text-slate-800'}`}>{status}</span>;
    };

    return (
        <div>
            <OrdersStats authHeader={authHeader} />
            <div className="flex gap-2 mb-4">
                <input
                    value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search email…"
                    className="flex-1 bg-white border border-border rounded-xl px-4 py-2 text-sm focus:border-primary outline-none"
                    onKeyDown={e => e.key === 'Enter' && load()}
                    data-testid="admin-orders-search"
                />
                <button onClick={load} className="bg-foreground text-white rounded-xl px-4 py-2 text-sm font-semibold">Search</button>
            </div>
            <div className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-3">ID</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Email</th>
                            <th className="text-left p-3">Amount</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-left p-3">Referral</th>
                            <th className="text-left p-3">Ship to</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map(o => (
                            <tr key={o.id} className="border-t border-border" data-testid={`admin-order-${o.id}`}>
                                <td className="p-3 font-mono text-xs">#{o.id.slice(-8).toUpperCase()}</td>
                                <td className="p-3 text-xs text-muted-foreground">{new Date(o.created_at).toLocaleDateString()}</td>
                                <td className="p-3">{o.email}</td>
                                <td className="p-3 font-semibold">€{o.amount?.toFixed(2)}</td>
                                <td className="p-3">{badge(o.status)}</td>
                                <td className="p-3 text-xs">
                                    {o.referral_code_used ? (
                                        <span className={`font-semibold ${o.referral_status === 'flagged' ? 'text-red-600' : 'text-primary'}`}>
                                            {o.referral_code_used} · {o.referral_status}
                                        </span>
                                    ) : <span className="text-muted-foreground">—</span>}
                                </td>
                                <td className="p-3 text-xs text-muted-foreground">
                                    {o.shipping_address ? (
                                        <span>{o.shipping_address.city}, {o.shipping_address.country}</span>
                                    ) : '—'}
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    {o.status !== 'shipped' && o.status !== 'refunded' && (
                                        <button onClick={() => ship(o.id)} disabled={actionId === o.id}
                                                className="inline-flex items-center gap-1 text-xs bg-green-600 text-white rounded-lg px-2.5 py-1.5 mr-1 hover:bg-green-700 disabled:opacity-50"
                                                data-testid={`ship-${o.id}`}>
                                            <Truck size={12} /> Ship
                                        </button>
                                    )}
                                    <button onClick={() => resend(o.id)} disabled={actionId === o.id}
                                            className="inline-flex items-center gap-1 text-xs bg-slate-200 text-slate-800 rounded-lg px-2.5 py-1.5 mr-1 hover:bg-slate-300 disabled:opacity-50">
                                        <Send size={12} />
                                    </button>
                                    {o.status !== 'refunded' && (
                                        <button onClick={() => refund(o.id)} disabled={actionId === o.id}
                                                className="inline-flex items-center gap-1 text-xs bg-red-600 text-white rounded-lg px-2.5 py-1.5 hover:bg-red-700 disabled:opacity-50"
                                                data-testid={`refund-${o.id}`}>
                                            <RefreshCw size={12} /> Refund
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {orders.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No orders yet.</td></tr>
                        )}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

function Customers({ authHeader }) {
    const [rows, setRows] = useState([]);
    useEffect(() => {
        axios.get(`${API}/admin/customers`, { headers: authHeader() })
            .then(r => setRows(r.data)).catch(console.error);
    }, []);
    return (
        <div className="bg-white rounded-xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                        <th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Name</th>
                        <th className="text-left p-3">Joined</th>
                        <th className="text-left p-3">Orders</th>
                        <th className="text-left p-3">Spent</th>
                        <th className="text-left p-3">Referral code</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(u => (
                        <tr key={u.id} className="border-t border-border">
                            <td className="p-3">{u.email}</td>
                            <td className="p-3 text-muted-foreground">{u.name || '—'}</td>
                            <td className="p-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                            <td className="p-3 font-semibold">{u.orders_count}</td>
                            <td className="p-3">€{(u.total_spent || 0).toFixed(2)}</td>
                            <td className="p-3 font-mono text-xs">{u.referral_code || '—'}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No customers yet.</td></tr>
                    )}
                </tbody>
            </table>
            </div>
        </div>
    );
}

function Referrals({ authHeader }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/referrals`, { headers: authHeader() })
            .then(r => setData(r.data)).catch(console.error);
    }, []);
    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-6">
            <div>
                <h3 className="font-heading text-lg font-black mb-3">Top referrers</h3>
                <div className="bg-white rounded-xl border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Email</th>
                                <th className="text-left p-3">Code</th>
                                <th className="text-left p-3">Earned</th>
                                <th className="text-left p-3">Pending</th>
                                <th className="text-left p-3">Available</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.top_referrers.map(u => (
                                <tr key={u.id} className="border-t border-border">
                                    <td className="p-3">{u.email}</td>
                                    <td className="p-3 font-mono text-xs">{u.referral_code}</td>
                                    <td className="p-3 font-semibold">{u.referrals_count}</td>
                                    <td className="p-3">{u.referral_credits_pending}</td>
                                    <td className="p-3 text-primary font-semibold">{u.referral_credits_available}</td>
                                </tr>
                            ))}
                            {data.top_referrers.length === 0 && (
                                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">No activity yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {data.flagged_orders.length > 0 && (
                <div>
                    <h3 className="font-heading text-lg font-black mb-3 text-red-600 flex items-center gap-2">
                        <AlertCircle size={18} /> Flagged orders ({data.flagged_orders.length})
                    </h3>
                    <div className="bg-white rounded-xl border border-red-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <tbody>
                                {data.flagged_orders.map(o => (
                                    <tr key={o.id} className="border-t border-border">
                                        <td className="p-3 font-mono text-xs">#{o.id.slice(-8).toUpperCase()}</td>
                                        <td className="p-3">{o.email}</td>
                                        <td className="p-3 text-xs text-muted-foreground">{o.referral_code_used}</td>
                                        <td className="p-3 text-xs text-red-600">Duplicate shipping fingerprint</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

const EMPTY_ARTICLE = {
    slug: '', title_en: '', title_nl: '', excerpt_en: '', excerpt_nl: '',
    content_en: '', content_nl: '', author: 'Sleepstack',
    author_bio_en: '', author_bio_nl: '', image_url: '', tags: [], read_time: 5,
    published: true, published_at: new Date().toISOString().slice(0, 10),
};

function Articles({ authHeader }) {
    const [arts, setArts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const r = await axios.get(`${API}/admin/articles`, { headers: authHeader() });
        setArts(r.data);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const body = { ...editing, tags: typeof editing.tags === 'string' ? editing.tags.split(',').map(t => t.trim()).filter(Boolean) : editing.tags };
            if (editing.id && arts.find(a => a.id === editing.id)) {
                await axios.put(`${API}/admin/articles/${editing.id}`, body, { headers: authHeader() });
            } else {
                await axios.post(`${API}/admin/articles`, body, { headers: authHeader() });
            }
            setEditing(null);
            await load();
        } catch (e) { alert('Save failed: ' + (e?.response?.data?.detail || e.message)); }
        finally { setSaving(false); }
    };
    const togglePublish = async (a) => {
        await axios.put(`${API}/admin/articles/${a.id}`, { published: !(a.published !== false) }, { headers: authHeader() });
        await load();
    };
    const remove = async (id) => {
        if (!window.confirm('Delete this article? This cannot be undone.')) return;
        await axios.delete(`${API}/admin/articles/${id}`, { headers: authHeader() });
        await load();
    };

    if (editing) {
        const f = (k) => editing[k] || '';
        const set = (k, v) => setEditing(p => ({ ...p, [k]: v }));
        const input = 'w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-primary outline-none';
        return (
            <div className="bg-white rounded-xl border border-border p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading text-xl font-black">{editing.id ? 'Edit article' : 'New article'}</h3>
                    <button onClick={() => setEditing(null)} className="text-sm text-muted-foreground hover:text-foreground">Cancel</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Slug</label>
                        <input className={input} value={f('slug')} onChange={e => set('slug', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Author</label>
                        <input className={input} value={f('author')} onChange={e => set('author', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Title (EN)</label>
                        <input className={input} value={f('title_en')} onChange={e => set('title_en', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Title (NL)</label>
                        <input className={input} value={f('title_nl')} onChange={e => set('title_nl', e.target.value)} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Excerpt (EN)</label>
                        <textarea className={input + ' min-h-[80px]'} value={f('excerpt_en')} onChange={e => set('excerpt_en', e.target.value)} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Excerpt (NL)</label>
                        <textarea className={input + ' min-h-[80px]'} value={f('excerpt_nl')} onChange={e => set('excerpt_nl', e.target.value)} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Content (EN) — HTML or Markdown</label>
                        <textarea className={input + ' min-h-[240px] font-mono text-xs'} value={f('content_en')} onChange={e => set('content_en', e.target.value)} /></div>
                    <div className="md:col-span-2"><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Content (NL)</label>
                        <textarea className={input + ' min-h-[240px] font-mono text-xs'} value={f('content_nl')} onChange={e => set('content_nl', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Image URL</label>
                        <input className={input} value={f('image_url')} onChange={e => set('image_url', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Tags (comma separated)</label>
                        <input className={input} value={Array.isArray(editing.tags) ? editing.tags.join(', ') : (editing.tags || '')} onChange={e => set('tags', e.target.value)} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Read time (min)</label>
                        <input type="number" className={input} value={f('read_time')} onChange={e => set('read_time', Number(e.target.value))} /></div>
                    <div><label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Published at</label>
                        <input className={input} value={f('published_at')} onChange={e => set('published_at', e.target.value)} placeholder="YYYY-MM-DD" /></div>
                    <div className="md:col-span-2 flex items-center gap-2">
                        <input id="pub" type="checkbox" checked={editing.published !== false} onChange={e => set('published', e.target.checked)} />
                        <label htmlFor="pub" className="text-sm">Published (visible on the public blog)</label>
                    </div>
                </div>
                <div className="flex gap-2 mt-6">
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-article">
                        {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditing(null)} className="bg-white border border-border rounded-full px-6 py-3 text-sm font-semibold">
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-heading text-lg font-black">{arts.length} articles</h3>
                <button onClick={() => setEditing({ ...EMPTY_ARTICLE })} className="btn-primary flex items-center gap-2" data-testid="new-article">
                    <Plus size={15} /> New article
                </button>
            </div>
            <div className="bg-white rounded-xl border border-border overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                        <tr>
                            <th className="text-left p-3">Title</th>
                            <th className="text-left p-3">Slug</th>
                            <th className="text-left p-3">Date</th>
                            <th className="text-left p-3">Tags</th>
                            <th className="text-left p-3">Status</th>
                            <th className="text-right p-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {arts.map(a => (
                            <tr key={a.id} className="border-t border-border">
                                <td className="p-3">{a.title_en || a.title_nl || '(untitled)'}</td>
                                <td className="p-3 font-mono text-xs text-muted-foreground">{a.slug}</td>
                                <td className="p-3 text-xs text-muted-foreground">{a.published_at}</td>
                                <td className="p-3 text-xs text-muted-foreground">{(a.tags || []).join(', ')}</td>
                                <td className="p-3">
                                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${a.published !== false ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-600'}`}>
                                        {a.published !== false ? 'published' : 'draft'}
                                    </span>
                                </td>
                                <td className="p-3 text-right whitespace-nowrap">
                                    <button onClick={() => togglePublish(a)}
                                            className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-lg px-2.5 py-1.5 mr-1 hover:bg-slate-200">
                                        {a.published !== false ? <EyeOff size={12} /> : <Eye size={12} />}
                                    </button>
                                    <button onClick={() => setEditing({ ...a, tags: a.tags || [] })}
                                            className="inline-flex items-center gap-1 text-xs bg-slate-100 rounded-lg px-2.5 py-1.5 mr-1 hover:bg-slate-200"
                                            data-testid={`edit-article-${a.id}`}>
                                        <Edit3 size={12} />
                                    </button>
                                    <button onClick={() => remove(a.id)}
                                            className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-600 rounded-lg px-2.5 py-1.5 hover:bg-red-100">
                                        <Trash2 size={12} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function NotifySignups({ authHeader, currentEmail }) {
    const [data, setData] = useState(null);
    const [composing, setComposing] = useState(false);
    const [form, setForm] = useState({
        subject_en: '',
        subject_nl: '',
        body_en: '',
        body_nl: '',
    });
    const [sending, setSending] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/admin/notify-signups`, { headers: authHeader() });
            setData(r.data);
        } catch (e) { console.error(e); }
    };
    useEffect(() => { load(); }, []);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const canSend = form.subject_en.trim() && form.subject_nl.trim() && form.body_en.trim() && form.body_nl.trim();

    const sendTest = async () => {
        if (!canSend) return;
        setSending(true);
        setLastResult(null);
        try {
            const r = await axios.post(
                `${API}/admin/notify-signups/blast`,
                { ...form, test_email: currentEmail },
                { headers: authHeader() },
            );
            setLastResult(r.data);
        } catch (e) {
            setLastResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setSending(false);
        }
    };

    const sendBlast = async () => {
        if (!canSend || !data) return;
        const ok = window.confirm(
            `Blast this launch email to ALL ${data.total} notify-me subscribers?\n\n` +
            `${data.count_en} will get the EN version, ${data.count_nl} the NL version.\n\n` +
            `This cannot be undone.`
        );
        if (!ok) return;
        setSending(true);
        setLastResult(null);
        try {
            const r = await axios.post(
                `${API}/admin/notify-signups/blast`,
                form,
                { headers: authHeader() },
            );
            setLastResult(r.data);
            await load();
        } catch (e) {
            setLastResult({ error: e?.response?.data?.detail || e.message });
        } finally {
            setSending(false);
        }
    };

    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

    const rows = data.signups || [];
    const input = 'w-full bg-white border border-border rounded-lg px-3 py-2 text-sm focus:border-primary outline-none';

    return (
        <div className="space-y-6" data-testid="notify-signups-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Stat label="Signups (total)" value={data.total} accent />
                <Stat label="EN subscribers" value={data.count_en} />
                <Stat label="NL subscribers" value={data.count_nl} />
                <Stat label="Already blasted" value={data.blasted} />
            </div>

            <div className="card-base p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="max-w-xl">
                        <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-1">
                            Launch announcement
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                            Compose a bilingual launch email. Each subscriber receives it in the language
                            they signed up with (fallback: EN). Send a test to your own address first.
                        </p>
                    </div>
                    {!composing && (
                        <button
                            onClick={() => setComposing(true)}
                            className="btn-primary flex items-center gap-2"
                            data-testid="open-blast-composer"
                        >
                            <Send size={14} /> Compose blast
                        </button>
                    )}
                </div>

                {composing && (
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Subject (EN)</label>
                            <input className={input} value={form.subject_en} onChange={e => set('subject_en', e.target.value)} data-testid="blast-subject-en" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Subject (NL)</label>
                            <input className={input} value={form.subject_nl} onChange={e => set('subject_nl', e.target.value)} data-testid="blast-subject-nl" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Body (EN) — HTML allowed</label>
                            <textarea className={input + ' min-h-[160px] font-mono text-xs'} value={form.body_en} onChange={e => set('body_en', e.target.value)} data-testid="blast-body-en" />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Body (NL) — HTML allowed</label>
                            <textarea className={input + ' min-h-[160px] font-mono text-xs'} value={form.body_nl} onChange={e => set('body_nl', e.target.value)} data-testid="blast-body-nl" />
                        </div>
                        <div className="md:col-span-2 flex gap-2 flex-wrap">
                            <button
                                onClick={sendTest}
                                disabled={!canSend || sending}
                                className="bg-white border border-border rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
                                data-testid="send-blast-test"
                            >
                                {sending ? 'Sending…' : `Send test to ${currentEmail}`}
                            </button>
                            <button
                                onClick={sendBlast}
                                disabled={!canSend || sending || data.total === 0}
                                className="btn-primary disabled:opacity-50 flex items-center gap-2"
                                data-testid="send-blast-live"
                            >
                                <Send size={14} /> {sending ? 'Sending…' : `Blast to ${data.total} subscribers`}
                            </button>
                            <button
                                onClick={() => { setComposing(false); setLastResult(null); }}
                                className="text-sm text-muted-foreground hover:text-foreground px-3"
                            >
                                Cancel
                            </button>
                        </div>
                        {lastResult && (
                            <div className="md:col-span-2 text-xs bg-slate-50 border border-border rounded-lg p-3" data-testid="blast-result">
                                {lastResult.error ? (
                                    <span className="text-red-600 font-semibold">Failed: {lastResult.error}</span>
                                ) : lastResult.mode === 'test' ? (
                                    <span className="text-primary font-semibold">Test sent to {lastResult.to} · EN={String(lastResult.sent_en)} · NL={String(lastResult.sent_nl)}</span>
                                ) : (
                                    <span className="text-primary font-semibold">Blast finished · sent {lastResult.sent}/{lastResult.recipient_count} (failed {lastResult.failed})</span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-white rounded-xl border border-border overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Email</th>
                                <th className="text-left p-3">Lang</th>
                                <th className="text-left p-3">Source</th>
                                <th className="text-left p-3">Signed up</th>
                                <th className="text-left p-3">Submits</th>
                                <th className="text-left p-3">Last blast</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(s => (
                                <tr key={s.email} className="border-t border-border" data-testid={`notify-row-${s.email}`}>
                                    <td className="p-3">{s.email}</td>
                                    <td className="p-3">
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 uppercase">{s.lang || 'en'}</span>
                                    </td>
                                    <td className="p-3 text-xs text-muted-foreground">{s.source || '—'}</td>
                                    <td className="p-3 text-xs text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}</td>
                                    <td className="p-3 text-xs">{s.signup_count || 1}</td>
                                    <td className="p-3 text-xs text-muted-foreground">
                                        {s.last_blast_at ? new Date(s.last_blast_at).toLocaleDateString() : '—'}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No signups yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Subscriptions({ authHeader }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        axios.get(`${API}/admin/subscriptions`, { headers: authHeader() })
            .then(r => setData(r.data)).catch(console.error);
    }, []);
    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
    return (
        <div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
                <div className="flex items-start gap-3">
                    <AlertCircle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900 leading-relaxed">
                        {data.note}
                    </p>
                </div>
            </div>
            {data.subscriptions?.length > 0 ? (
                <div className="bg-white rounded-xl border border-border overflow-hidden">
                    {/* Placeholder table header for future schema */}
                </div>
            ) : (
                <div className="text-center py-12 card-base">
                    <CreditCard size={32} className="text-border mx-auto mb-3" />
                    <p className="text-sm text-muted-foreground">No subscriptions yet.</p>
                </div>
            )}
        </div>
    );
}

export default function Admin() {
    const { user, loading, authHeader, logout } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState('orders');
    const [denied, setDenied] = useState(false);

    useEffect(() => {
        if (!loading && !user) {
            navigate('/login?redirect=/admin');
            return;
        }
        if (!loading && user) {
            // Check admin by calling one admin endpoint
            axios.get(`${API}/admin/metrics`, { headers: authHeader() })
                .then(() => setDenied(false))
                .catch(err => {
                    if (err?.response?.status === 403) setDenied(true);
                });
        }
    }, [user, loading]);

    if (loading || !user) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-muted-foreground text-sm">Loading…</div>
            </div>
        );
    }

    if (denied) {
        return (
            <div>
                <PreSaleBanner />
                <Navbar />
                <main style={{ paddingTop: 'var(--page-offset, 128px)' }}>
                    <section className="section-padding">
                        <div className="section-container max-w-xl text-center">
                            <AlertCircle size={36} className="text-red-500 mx-auto mb-3" />
                            <h1 className="heading-lg text-foreground mb-3">Admin only</h1>
                            <p className="text-muted-foreground mb-6">
                                Your account ({user.email}) is not whitelisted for the admin dashboard.
                            </p>
                            <button onClick={logout} className="btn-primary">Log out</button>
                        </div>
                    </section>
                </main>
                <Footer />
            </div>
        );
    }

    return (
        <div>
            <SEO title="Admin | Sleepstack" path="/admin" />
            <PreSaleBanner />
            <Navbar />
            <main style={{ paddingTop: 'var(--page-offset, 128px)' }}>
                <section className="section-padding">
                    <div className="section-container">
                        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                            <div>
                                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-2">
                                    <span className="w-8 h-px bg-primary" />
                                    Admin
                                </div>
                                <h1 className="heading-lg text-foreground">Sleepstack control</h1>
                            </div>
                            <div className="flex items-center gap-3 text-sm">
                                <span className="text-muted-foreground">{user.email}</span>
                                <span className="text-xs bg-primary/10 text-primary font-bold px-2.5 py-1 rounded-full uppercase tracking-widest">Admin</span>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="flex gap-1 bg-white border border-border rounded-xl p-1 mb-8 flex-wrap" data-testid="admin-tabs">
                            {TABS.map(tk => (
                                <button
                                    key={tk.key}
                                    onClick={() => setTab(tk.key)}
                                    data-testid={`admin-tab-${tk.key}`}
                                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                                        tab === tk.key ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'
                                    }`}
                                >
                                    <tk.icon size={14} /> {tk.label}
                                </button>
                            ))}
                        </div>

                        <motion.div
                            key={tab}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.25 }}
                        >
                            {tab === 'orders' && <Orders authHeader={authHeader} />}
                            {tab === 'customers' && <Customers authHeader={authHeader} />}
                            {tab === 'referrals' && <Referrals authHeader={authHeader} />}
                            {tab === 'articles' && <Articles authHeader={authHeader} />}
                            {tab === 'notify' && <NotifySignups authHeader={authHeader} currentEmail={user.email} />}
                            {tab === 'subscriptions' && <Subscriptions authHeader={authHeader} />}
                        </motion.div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
